import { Box, Button, Paper, Skeleton, Stack } from '@mui/material';
import { type GridApiPro, GridRow, useGridApiRef } from '@mui/x-data-grid-pro';
import { withDefault } from '@vy/utils';
import { Helmet } from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { AddIcon } from '../../../components/icons/AddIcon';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useDialog } from '../../../hooks/use-dialog';
import { useTablePagination } from '../../../hooks/use-table-pagination';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { PlanSelectionDialog } from '../../plans/GetStartedPlansPage/components/CardsSections/components/PlanSelectionDialog';
import { useTableContracts } from '../../plans/PlansPage/plans-page-components/ContractsTabContent/use-table-contracts';
import { ContractsTable } from '../../plans/PlansPage/plans-page-components/ContractsTable';
import { CONTRACTS_COLUMNS_KEYS } from '../../plans/PlansPage/plans-page-consts/contracts-columns';
import { useContractsFilters } from '../../plans/PlansPage/plans-page-hooks/use-contracts-filters';
import { TABLE_NAMES } from '@vy/types';

const CustomerContractsPage: React.FC = () => {
  const gridApiRef = useGridApiRef();
  const filters = useContractsFilters();
  const { page, pageSize, onPageChange, onPageSizeChange } = useTablePagination();

  const { customerId } = useValidatedParams<['customerId']>('customerId');

  const { data: contractsResponse, isFetching } = useTableContracts(page, pageSize, customerId);

  const { isDialogOpen: isCreateContractDialogOpen, handleOpen: handleCreateContractDialogOpen, handleClose: handleCreateContractDialogClose } = useDialog();
  const contracts = withDefault(contractsResponse?.contracts, []);


  return (
    <>
      <Helmet>
        <title>Customer Contracts</title>
      </Helmet>
      <Paper
        sx={{
          borderRadius: '10px',
          height: '70vh',
          overflow: 'hidden',
          marginTop: '16px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ContractsTable
            tableName={TABLE_NAMES.CONTRACTS}
            apiRef={gridApiRef as React.MutableRefObject<GridApiPro>}
            filters={filters}
            loadingItems={isFetching}
            items={contracts}
            itemsCount={withDefault(contractsResponse?.countedFilteredContracts, 0)}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            slots={{
              row: (rowProps) => {
                const { id, customer } = rowProps.row;

                return (
                  <NavLink to={`/customers/${customer.id}/contracts/${id}`}>
                    <GridRow {...rowProps} />
                  </NavLink>
                );
              },
            }}
            showSearch={false}
            disableExport
            disableFilter
            excludedColumns={[CONTRACTS_COLUMNS_KEYS.CUSTOMER_ID, CONTRACTS_COLUMNS_KEYS.CUSTOMER_NAME]}
            renderActions={() => (
              <Stack direction="row" sx={{
                alignItems: "flex-end"
              }}>
                <Button startIcon={<AddIcon fill='#fff' />} variant="contained" onClick={handleCreateContractDialogOpen}>
                  New Contract
                </Button>
              </Stack>
            )
            }
          />
        </Box>

      </Paper>
      <PlanSelectionDialog isOpen={isCreateContractDialogOpen} onClose={handleCreateContractDialogClose} initialCustomerId={customerId} />
    </>
  );
};

const CustomerContractsPageWithGlobalSearchParams = withGlobalSearchParams(CustomerContractsPage);

const CustomerContractsPageWithSuspenseBoundary = withSuspenseBoundary(
  CustomerContractsPageWithGlobalSearchParams,
  {
    suspenseProps: {
      fallback: (
        <Skeleton
          variant="rectangular"
          sx={{ borderRadius: '10px', height: '700px', marginTop: '16px', position: 'relative' }}
        />
      ),
    },
  },
);

export { CustomerContractsPageWithSuspenseBoundary as CustomerContractsPage };
